import React, { useEffect, useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle2, Circle, Trash2, Plus, Sparkles, X, ListChecks, CalendarClock, Calendar, Bell } from 'lucide-react';
import { toast } from 'sonner';
import { todoAPI } from '../utils/api';

const catStyles = {
  unconfirmed: 'text-amber-500',
  missing_crew: 'text-blue-500',
  unpaid: 'text-emerald-500',
  overdue: 'text-red-500',
};

const todayStr = () => new Date().toISOString().slice(0, 10);

const dueInfo = (due) => {
  if (!due) return null;
  const d = new Date(due + 'T00:00:00');
  const today = new Date(todayStr() + 'T00:00:00');
  const diff = Math.round((d - today) / 86400000);
  const label =
    diff === 0 ? 'Today'
    : diff === 1 ? 'Tomorrow'
    : diff === -1 ? 'Yesterday'
    : diff < 0 ? `${Math.abs(diff)}d overdue`
    : d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  const tone =
    diff < 0 ? 'bg-red-500/10 text-red-500 border-red-500/20'
    : diff === 0 ? 'bg-amber-500/10 text-amber-500 border-amber-500/20'
    : 'bg-secondary text-muted-foreground border-transparent';
  return { label, tone, overdue: diff < 0, soon: diff <= 1 };
};

export const TodoCard = () => {
  const [todos, setTodos] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [newTask, setNewTask] = useState('');
  const [newDue, setNewDue] = useState('');
  const [showDue, setShowDue] = useState(false);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);

  const load = useCallback(async () => {
    try {
      const [t, s] = await Promise.all([todoAPI.list(), todoAPI.suggestions()]);
      setTodos(t.data || []);
      setSuggestions(s.data || []);
    } catch {
      /* silent */
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const addTask = async (e) => {
    e.preventDefault();
    const text = newTask.trim();
    if (!text) return;
    setAdding(true);
    try {
      const { data } = await todoAPI.create(text, newDue || null);
      setTodos((prev) => [data, ...prev]);
      setNewTask('');
      setNewDue('');
      setShowDue(false);
    } catch {
      toast.error('Could not add task');
    } finally {
      setAdding(false);
    }
  };

  const toggle = async (todo) => {
    const done = !todo.done;
    setTodos((prev) => prev.map((t) => (t.todo_id === todo.todo_id ? { ...t, done } : t)));
    try { await todoAPI.update(todo.todo_id, { done }); } catch { load(); }
  };

  const setDue = async (todo, due) => {
    setTodos((prev) => prev.map((t) => (t.todo_id === todo.todo_id ? { ...t, due_date: due || null } : t)));
    try {
      await todoAPI.update(todo.todo_id, { due_date: due || '' });
      if (due) toast.success('Reminder set 🔔');
    } catch { load(); }
  };

  const remove = async (id) => {
    setTodos((prev) => prev.filter((t) => t.todo_id !== id));
    try { await todoAPI.delete(id); } catch { load(); }
  };

  const accept = async (s) => {
    setSuggestions((prev) => prev.filter((x) => x.suggestion_key !== s.suggestion_key));
    try {
      const { data } = await todoAPI.acceptSuggestion(s.suggestion_key, s.text);
      setTodos((prev) => [data, ...prev]);
      toast.success('Added to your list');
    } catch { load(); }
  };

  const dismiss = async (s) => {
    setSuggestions((prev) => prev.filter((x) => x.suggestion_key !== s.suggestion_key));
    try { await todoAPI.dismissSuggestion(s.suggestion_key); } catch { load(); }
  };

  const activeTodos = todos.filter((t) => !t.done);
  const doneTodos = todos.filter((t) => t.done);

  return (
    <div className="card p-4 sm:p-6 mb-8" data-testid="todo-card">
      <div className="flex items-center gap-2 mb-4 sm:mb-5">
        <ListChecks className="text-gold" size={20} />
        <h2 className="text-xl sm:text-2xl font-semibold tracking-tight">To-Do</h2>
        {activeTodos.length > 0 && (
          <span className="ml-auto text-sm font-medium text-muted-foreground" data-testid="todo-active-count">
            {activeTodos.length} open
          </span>
        )}
      </div>

      {/* Add task */}
      <form onSubmit={addTask} className="mb-4">
        <div className="flex items-center gap-2">
          <input
            value={newTask}
            onChange={(e) => setNewTask(e.target.value)}
            placeholder="Add a task…"
            className="flex-1 px-4 py-3 rounded-2xl bg-secondary border border-transparent focus:border-ring/40 focus:ring-2 focus:ring-ring/20 text-sm outline-none text-foreground"
            data-testid="todo-input"
          />
          <button
            type="button"
            onClick={() => setShowDue((v) => !v)}
            className={`p-3 rounded-2xl transition-all active:scale-90 ${showDue || newDue ? 'bg-gold/15 text-gold' : 'bg-secondary text-muted-foreground'}`}
            data-testid="todo-due-toggle"
            aria-label="Set due date"
          >
            <CalendarClock size={18} />
          </button>
          <button
            type="submit"
            disabled={adding || !newTask.trim()}
            className="p-3 rounded-2xl bg-primary text-primary-foreground transition-all active:scale-90 disabled:opacity-40"
            data-testid="todo-add-button"
            aria-label="Add task"
          >
            <Plus size={18} />
          </button>
        </div>
        <AnimatePresence initial={false}>
          {(showDue || newDue) && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="flex items-center gap-2 mt-2 pl-1"
            >
              <Calendar size={15} className="text-gold shrink-0" />
              <span className="text-xs font-medium text-muted-foreground">Remind me on</span>
              <input
                type="date"
                value={newDue}
                min={todayStr()}
                onChange={(e) => setNewDue(e.target.value)}
                className="px-3 py-1.5 rounded-xl bg-secondary border border-transparent focus:border-ring/40 text-sm outline-none text-foreground"
                data-testid="todo-due-input"
              />
              {newDue && (
                <button
                  type="button"
                  onClick={() => setNewDue('')}
                  className="p-1 rounded-full text-muted-foreground hover:text-destructive transition-colors active:scale-90"
                  data-testid="todo-due-clear"
                  aria-label="Clear due date"
                >
                  <X size={15} />
                </button>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </form>

      {/* Smart suggestions */}
      <AnimatePresence initial={false}>
        {suggestions.length > 0 && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="mb-4"
            data-testid="todo-suggestions"
          >
            <div className="flex items-center gap-1.5 mb-2">
              <Sparkles size={14} className="text-gold" />
              <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Suggested</span>
            </div>
            <div className="space-y-2">
              <AnimatePresence initial={false}>
                {suggestions.map((s) => (
                  <motion.div
                    key={s.suggestion_key}
                    layout
                    initial={{ opacity: 0, y: 6 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                    className="flex items-center gap-3 p-3 rounded-2xl bg-gold/10 border border-gold/20"
                    data-testid={`suggestion-${s.suggestion_key}`}
                  >
                    <Sparkles size={16} className={catStyles[s.category] || 'text-gold'} />
                    <span className="flex-1 text-sm text-foreground leading-snug">{s.text}</span>
                    <button
                      onClick={() => accept(s)}
                      className="px-3 py-1.5 rounded-full bg-primary text-primary-foreground text-xs font-medium transition-all active:scale-90"
                      data-testid={`suggestion-accept-${s.suggestion_key}`}
                    >
                      Add
                    </button>
                    <button
                      onClick={() => dismiss(s)}
                      className="p-1.5 rounded-full text-muted-foreground hover:bg-secondary dark:hover:bg-card/10 transition-colors active:scale-90"
                      data-testid={`suggestion-dismiss-${s.suggestion_key}`}
                      aria-label="Dismiss suggestion"
                    >
                      <X size={16} />
                    </button>
                  </motion.div>
                ))}
              </AnimatePresence>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Tasks */}
      {loading ? (
        <div className="space-y-2">
          {[1, 2].map((i) => <div key={i} className="h-12 rounded-2xl bg-secondary animate-pulse" />)}
        </div>
      ) : activeTodos.length === 0 && doneTodos.length === 0 && suggestions.length === 0 ? (
        <p className="text-sm text-muted-foreground py-4 text-center" data-testid="todo-empty">
          You're all caught up. Nice. ✨
        </p>
      ) : (
        <div className="space-y-2">
          <AnimatePresence initial={false}>
            {[...activeTodos, ...doneTodos].map((t) => (
              <motion.div
                key={t.todo_id}
                layout
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="group flex items-center gap-3 p-3 rounded-2xl hover:bg-secondary transition-colors"
                data-testid={`todo-item-${t.todo_id}`}
              >
                <button
                  onClick={() => toggle(t)}
                  className="shrink-0 transition-transform active:scale-90"
                  data-testid={`todo-toggle-${t.todo_id}`}
                  aria-label={t.done ? 'Mark not done' : 'Mark done'}
                >
                  {t.done
                    ? <CheckCircle2 size={22} className="text-gold" />
                    : <Circle size={22} className="text-muted-foreground" />}
                </button>
                <span className={`flex-1 text-sm leading-snug ${t.done ? 'line-through text-muted-foreground' : 'text-foreground'}`}>
                  {t.text}
                </span>
                {!t.done && (() => {
                  const info = dueInfo(t.due_date);
                  return (
                    <label
                      className={`relative shrink-0 inline-flex items-center gap-1 px-2 py-1 rounded-full border text-xs font-medium cursor-pointer transition-all active:scale-90 ${info ? info.tone : 'text-muted-foreground border-transparent opacity-0 group-hover:opacity-100 hover:bg-secondary'}`}
                      data-testid={`todo-due-badge-${t.todo_id}`}
                      title={info ? 'Change reminder date' : 'Set a reminder date'}
                    >
                      <CalendarClock size={13} />
                      {info && <span>{info.label}</span>}
                      <input
                        type="date"
                        value={t.due_date || ''}
                        min={todayStr()}
                        onChange={(e) => setDue(t, e.target.value)}
                        className="absolute inset-0 opacity-0 cursor-pointer"
                        data-testid={`todo-due-edit-${t.todo_id}`}
                        aria-label="Set due date"
                      />
                    </label>
                  );
                })()}
                {t.source === 'suggestion' && !t.done && (
                  <Sparkles size={13} className="text-gold shrink-0" />
                )}
                {t.source === 'lead_followup' && !t.done && (
                  <Bell size={13} className="text-gold shrink-0" />
                )}
                <button
                  onClick={() => remove(t.todo_id)}
                  className="p-1.5 rounded-full text-muted-foreground opacity-0 group-hover:opacity-100 hover:text-destructive transition-all active:scale-90"
                  data-testid={`todo-delete-${t.todo_id}`}
                  aria-label="Delete task"
                >
                  <Trash2 size={16} />
                </button>
              </motion.div>
            ))}
          </AnimatePresence>
        </div>
      )}
    </div>
  );
};

export default TodoCard;
